import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import {
    IconArrowRight,
    IconChevronSprite,
    IconChevronDown,
} from '$partials/icons/index.js';

import Image from '$partials/media/image.jsx';
import ModuleWalkthroughMobile from './walkthrough-mobile.jsx';
import Button from '$partials/ui/button.jsx';
import { useLocale } from '$contexts/locale.jsx';

gsap.registerPlugin(useGSAP);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const ModuleWalkthrough = ({
    eyebrow,
    title,
    itemsCollection,
    itemsVariant,
    mainCtaLabel,
    mainCtaLink,
    standfirst,
    parent
}) => {
    const { formatURI } = useLocale();

    const containerRef = useRef();

    const [activePanel, setActivePanel] = useState(0);
    const [windowSize, setWindowSize] = useState({ height: undefined, width: undefined });

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth !== windowSize.width) {
                setWindowSize({ height: window.innerHeight, width: window.innerWidth });
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [windowSize.width]);

    useGSAP(() => {
        const easing = 'power4.out';
        // Resizing panels
        gsap.to('.tab-panel[data-selected] .tab-panel-text', {
            height: 'auto',
            ease: easing,
            duration: 0.4
        });
        gsap.to('.tab-panel:not([data-selected]) .tab-panel-text', {
            height: 0,
            ease: easing,
            duration: 0.4
        });

        // Fading panel texts
        gsap.to('.tab-panel[data-selected] .tab-panel-text p', {
            opacity: 1,
            duration: 0.4
        });
        gsap.to('.tab-panel:not([data-selected]) .tab-panel-text p', {
            opacity: 0,
            duration: 0.2
        });

        // Cross-fading illustration
        gsap.to('.tab-panel:not([data-selected]) .tab-panel-illustration', {
            opacity: 0,
            duration: 0.3
        });
        gsap.to('.tab-panel[data-selected] .tab-panel-illustration', {
            opacity: 1,
            duration: 0.3,
        });
    }, {
        dependencies: [activePanel],
        scope: containerRef
    });

    return (
        <>
            <section className={`hidden md:block relative ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
                <div
                    className='layout-container'
                    ref={containerRef}>

                    <div className=''>
                        <div className='module-walk-through'>
                            {eyebrow && (
                                <h1
                                    className='type-style-11 text-core-brand px-10 sm:px-5 md:px-10'>
                                    {eyebrow}
                                </h1>
                            )}

                            <h2 className='type-style-2 text-grey-black mt-10 px-10 sm:px-5 md:px-10'>
                                {title}
                            </h2>

                            {standfirst && (
                                <p className='type-style-8 text-grey-off-black mt-12 px-10 sm:px-5 md:px-10 sm:w-10/12 md:w-8/12'>
                                    {standfirst}
                                </p>
                            )}

                            {itemsCollection?.items?.length > 0 && (
                                <TabGroup
                                    vertical
                                    onChange={(index) => {
                                        setActivePanel(index);
                                    }}>
                                    <TabList>
                                        <TabPanels className='flex relative md:px-10 mt-50'>
                                            <div className='flex flex-col w-5/12 gap-16'>
                                                {itemsCollection.items.map((itm, idx) => {
                                                    return (
                                                        <div
                                                            className='bg-grey mr-12 min-h-[84px] rounded-button'
                                                            key={idx}>
                                                            <Tab className='type-style-4 font-[500] w-full p-[1.7rem] pb-5 pl-24 text-left outline-none group inline-flex gap-16 items-center'>
                                                                <span
                                                                    className='text-dark-grey group-hover:text-core-brand group-data-[selected]:text-core-brand'>
                                                                    {itemsVariant === 'Numbered' ?
                                                                        String(idx + 1).padStart(2, '0') :
                                                                        <IconChevronDown className='group-data-[selected]:-rotate-90 transition-transform' />
                                                                    }
                                                                </span>

                                                                <span
                                                                    className='group-hover:text-black transition-colors duration-100 group-data-[selected]:text-black text-dark-grey'>
                                                                    {itm.heading}
                                                                </span>
                                                            </Tab>
                                                            <TabPanel
                                                                className='tab-panel'
                                                                static={true}
                                                                unmount={false}>
                                                                <div
                                                                    className='tab-panel-text overflow-clip'
                                                                    style={{
                                                                        height: idx === 0 ? 'auto' : 0
                                                                    }}>
                                                                    <div className='pl-24 pr-30 pb-30'>
                                                                        {itm?.copy && (
                                                                            <p
                                                                                className='text-grey-off-black'>
                                                                                {itm.copy}
                                                                            </p>
                                                                        )}

                                                                        {itm?.ctaLabel && itm?.ctaLink && !mainCtaLink && !mainCtaLabel && (
                                                                            <p className='mt-25'>
                                                                                <a
                                                                                    className='inline-flex items-center gap-5 type-style-5 text-core-brand hover:text-black transition-colors duration-100'
                                                                                    href={formatURI(itm?.ctaLink)}>
                                                                                    <span>
                                                                                        {itm?.ctaLabel}
                                                                                    </span>
                                                                                    <span>
                                                                                        <IconArrowRight />
                                                                                    </span>
                                                                                </a>
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className='absolute top-0 right-0 w-7/12 pointer-events-none select-none'>
                                                                    <div className='px-10 sm:px-5 md:px-10 w-full tab-panel-illustration'>
                                                                        {itm?.media?.contentType?.includes('video/') ?
                                                                            (
                                                                                <video
                                                                                    autoPlay
                                                                                    loop
                                                                                    muted
                                                                                    playsInline
                                                                                    className='w-full h-full object-cover aspect-square rounded-button'
                                                                                    src={itm?.media?.src}
                                                                                    width={'100%'} />
                                                                            ) : (
                                                                                <Image
                                                                                    {...itm?.media}
                                                                                    className='h-full w-full object-center object-cover select-none pointer-events-none aspect-square rounded-button'
                                                                                    containerClassName='w-full h-full relative'
                                                                                    height='100%'
                                                                                    loading=''
                                                                                    loadWidth={998}
                                                                                    width='100%' />
                                                                            )
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </TabPanel>
                                                        </div>
                                                    );
                                                })}

                                                {/* Button */}
                                                {mainCtaLabel && mainCtaLink && (
                                                    <div>
                                                        <Button
                                                            as='a'
                                                            className='button button--secondary-alt'
                                                            href={formatURI(mainCtaLink)}
                                                            type='button'>
                                                            <span>{mainCtaLabel}</span>
                                                            <span className='button__icon button__icon--animate'>
                                                                <IconChevronSprite />
                                                            </span>
                                                        </Button>
                                                    </div>
                                                )}
                                                {/* Mobile */}
                                            </div>
                                            {/* Spacer */}
                                            <div className='w-7/12'>
                                                <div className='aspect-square'></div>
                                            </div>
                                        </TabPanels>
                                    </TabList>
                                </TabGroup>
                            )}

                        </div>
                    </div>
                </div>
            </section>

            {(windowSize.width < 1024) && (
                <ModuleWalkthroughMobile
                    eyebrow={eyebrow}
                    itemsCollection={itemsCollection}
                    itemsVariant={itemsVariant}
                    mainCtaLabel={mainCtaLabel}
                    mainCtaLink={mainCtaLink}
                    parent={parent}
                    standfirst={standfirst}
                    title={title} />
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    itemsCollection: PropTypes.object,
    itemsVariant: PropTypes.string,
    mainCtaLabel: PropTypes.string,
    mainCtaLink: PropTypes.string,
    nextTypename: PropTypes.string,
    standfirst: PropTypes.string,
    parentTypename: PropTypes.string,
    parent: PropTypes.string,
};

ModuleWalkthrough.propTypes = propTypes;

export default ModuleWalkthrough;
